/**
 * A quantity, as a slider and the number it is on.
 *
 * The one control kind that exists. It is a native `input[type=range]`, so it arrives keyboard
 * driveable, screen-reader nameable and thumb sized without any of that being written here.
 *
 * The readout beside it is not decoration. A slider with no number is a setting a player can only
 * describe as "about a third along", which is useless the moment they sit at a second machine.
 *
 * `rangeApply` is the other half of the file, and it is here rather than in the contract because
 * this is where the kind is decided. A knob writes `apply: rangeApply(setThing)` and gets a
 * `SettingValue` narrowed to the number this control is the only thing that produces. Without it
 * every range knob would carry the same `typeof` guard, which is one rule kept in as many places as
 * there are knobs.
 *
 * Both `input` and `change`, and the difference between them is the whole design. `input` fires on
 * every pixel of a drag and applies, so the game turns at the new rate while the thumb is still
 * down: a sensitivity you have to let go of to feel is a sensitivity you tune by guessing. `change`
 * fires once, when the thumb comes up, and that is the only value written to the character. A save
 * per pixel is forty writes racing each other for one decision.
 */

import { el, setText } from '@/ui/dom';
import { clamp } from '@/sim/math';
import type {
  SettingControl,
  SettingControlDefinition,
  SettingControlHandle,
  SettingControlOptions,
  SettingValue,
} from '@/ui/settings/settingContract';

/**
 * A knob's `apply`, narrowed to what this control produces.
 *
 * Anything that is not a number never reaches the wrapped function. That cannot happen through the
 * screen or through a load, both of which go through `coerce` below; it is the type system being
 * told what `coerce` already guarantees, at the one place that knows it.
 */
export function rangeApply(apply: (value: number) => void): (value: SettingValue) => void {
  return (value: SettingValue): void => {
    if (typeof value === 'number') apply(value);
  };
}

export const RANGE_SLIDER: SettingControlDefinition = {
  kind: 'range',

  coerce(control: SettingControl, raw: unknown): SettingValue | null {
    // Finite on purpose: `NaN` and both infinities are numbers, and none of them is a setting.
    if (control.kind !== 'range') return null;
    if (typeof raw !== 'number' || !Number.isFinite(raw)) return null;
    return clamp(raw, control.min, control.max);
  },

  create(options: SettingControlOptions): SettingControlHandle {
    const control = options.definition.control;
    // The registry resolved this control by its own kind, so nothing else can arrive here. An
    // empty handle rather than a throw: a settings screen missing one row beats one that is blank.
    if (control.kind !== 'range') return { root: el('div', 'settings-control'), dispose: noop };

    const root = el('div', 'settings-control');

    const slider = el('input', 'settings-slider');
    slider.type = 'range';
    slider.min = String(control.min);
    slider.max = String(control.max);
    slider.step = String(control.step);
    slider.value = String(typeof options.value === 'number' ? options.value : control.min);
    slider.ariaLabel = options.definition.label;

    const readout = el(
      'output',
      'settings-readout',
      control.format(typeof options.value === 'number' ? options.value : control.min),
    );

    const current = (): number => clamp(slider.valueAsNumber, control.min, control.max);

    const onInput = (): void => {
      const value = current();
      setText(readout, control.format(value));
      options.onChange(value);
    };
    const onCommit = (): void => {
      options.onCommit(current());
    };
    slider.addEventListener('input', onInput);
    slider.addEventListener('change', onCommit);

    root.append(slider, readout);

    return {
      root,
      dispose(): void {
        slider.removeEventListener('input', onInput);
        slider.removeEventListener('change', onCommit);
        root.remove();
      },
    };
  },
};

/** What the unreachable branch in `create` hands back, so a missing row disposes like any other. */
function noop(): void {
  return undefined;
}
